import { Injectable } from '@angular/core';
import { Photographer } from '../models/new/photographer';
import { ColorShots, Order, OrderDetail, OrderFilters, OrderStatus } from '../models/new/order';
import { Status } from '../models/new/status';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { DateRange } from '../models/new/dateRange';
import { AspectRatio, CodifiedUse, MediaFormat, Shot, ShotType } from '../models/new/shot';

import { Color, Size, SizeGroup } from '../models/new/style';
import { Warehouse } from 'src/app/shared/types/warehouse.model';
import moment from 'moment';
import { StyleMapper } from './style-mapper';
@Injectable({
	providedIn: 'root'
})
export class OrderMapper {
	constructor(
		private utils: UtilitiesService,
		private styleMapper: StyleMapper
	) {}

	//da unificare
	toOrderSearch(order: any) {
		return new Order({
			id: order.orderId,
			status: new Status({ id: order.orderStatusId, name: order.orderStatusName }),
			dueDate: order.dueDate ? new Date(order.dueDate) : undefined,
			creationDate: order.creationDate ? new Date(order.creationDate) : undefined,
			completionDate: order.completionDate ? new Date(order.completionDate) : undefined,
			photographer: order.photographerPersonId ? new Photographer({ id: order.photographerPersonId, fullName: order.photographerFullName }) : undefined,
			notes: order.notes,
			warehouse: order.warehouse ? new Warehouse({ id: order.warehouse.id, code: order.warehouse.code, description: order.warehouse.label }) : undefined
		});
	}

	toOrder(order: any) {
		return new Order({
			id: order.orderId,
			status: new Status({ id: order.orderStatusId as OrderStatus, name: order.orderStatusName }),
			dueDate: order.dueDate ? new Date(order.dueDate) : undefined,
			creationDate: order.creationDate ? new Date(order.creationDate) : undefined,
			completionDate: order.completionDate ? new Date(order.completionDate) : undefined,
			photographer: order.photographerPersonId ? new Photographer({ id: order.photographerPersonId, fullName: order.photographerFullName }) : undefined,
			notes: order.notes,
			warehouse: order.warehouse ? new Warehouse({ id: order.warehouse.id, code: order.warehouse.code, description: order.warehouse.label }) : undefined
		});
	}

	toWhereClause(filters: OrderFilters) {
		return {
			orderId: filters.ids ? { in: filters.ids } : undefined,
			dueDate: filters.dueDate
				? {
						gte: moment(filters.dueDate.from).format('YYYY-MM-DD'),
						lte: moment(filters.dueDate.to).format('YYYY-MM-DD')
					}
				: undefined,
			creationDate: filters.creationDate ? { gte: moment(filters.creationDate.from).startOf('day').toDate(), lte: moment(filters.creationDate.to).endOf('day').toDate() } : undefined,
			completionDate: filters.completionDate ? { gte: moment(filters.completionDate.from).startOf('day').toDate(), lte: moment(filters.completionDate.to).endOf('day').toDate() } : undefined,
			photographerPersonId: filters.photographers?.length ? { in: filters.photographers.map((p) => p.id) } : undefined,
			orderStatusId: filters.statuses?.length ? { in: filters.statuses.map((s) => s.id) } : undefined,
			warehouse: filters.warehouses?.length ? { id: { in: filters.warehouses.map((w) => w.id) } } : undefined
		};
	}

	//da uniformare
	toWhereClausePhotographer(filters: OrderFilters) {
		return {
			orderId: filters.ids ? { in: filters.ids } : undefined,
			dueDate: filters.dueDate
				? {
						gte: moment(filters.dueDate.from).format('YYYY-MM-DD'),
						lte: moment(filters.dueDate.to).format('YYYY-MM-DD')
					}
				: undefined,
			creationDate: filters.creationDate ? { gte: moment(filters.creationDate.from).startOf('day').toDate(), lte: moment(filters.creationDate.to).endOf('day').toDate() } : undefined,
			completionDate: filters.completionDate ? { gte: moment(filters.completionDate.from).startOf('day').toDate(), lte: moment(filters.completionDate.to).endOf('day').toDate() } : undefined,
			photographerPersonId: filters.photographers?.length ? { in: filters.photographers.map((p) => p.id) } : undefined,
			orderStatusId: filters.statuses?.length ? { in: filters.statuses.map((s) => s.id).filter((id) => id !== OrderStatus.Draft) } : { neq: OrderStatus.Draft },
			warehouse: filters.warehouses?.length ? { id: { in: filters.warehouses.map((w) => w.id) } } : undefined
		};
	}

	toShot(shot: any) {
		return new Shot({
			id: shot.shotId,
			status: new Status({ id: shot.shotApprovalStatusId }),
			mandatory: shot.mandatory,
			imgPath: shot.imgPath,
			estimatedFileName: shot.estimatedFileName,
			colorId: shot.colorId,
			colorName: shot.colorName,
			modelId: shot.modelId,
			sizeGroup: new SizeGroup({
				id: shot.sizeGroupId || 0,
				name: shot.sizeGroupName || '-'
			}),
			shotApprovalStatus: shot.shotApprovalStatusId,
			codifiedUse: shot.codifiedUse ? new CodifiedUse({ id: shot.codifiedUse.id, name: shot.codifiedUse.label }) : undefined,
			mediaFormat: shot.mediaFormat ? new MediaFormat({ id: shot.mediaFormat.id, name: shot.mediaFormat.label }) : undefined,
			aspectRatio: shot.aspectRatio ? new AspectRatio({ id: shot.aspectRatio.id, label: shot.aspectRatio.label }) : undefined,
			shotType: shot.shotType ? new ShotType({ id: shot.shotType.id, name: shot.shotType.label }) : undefined
		});
	}

	toOrderDetail(orderDetail: any) {
		return new OrderDetail({
			style: this.styleMapper.toStyle(orderDetail.style),
			warehouse: orderDetail.warehouse ? new Warehouse({ id: orderDetail.warehouse.id, code: orderDetail.warehouse.code, description: orderDetail.warehouse.description }) : undefined,
			colorShots: orderDetail.colorsSizeGroups?.map(
				(csg: any) =>
					new ColorShots({
						color: new Color({ id: csg.colorId, name: csg.colorName, size: new Size({ id: csg.size.id, name: csg.size.label }) }),
						shots: csg.shots?.map((shot: any) => this.toShot(shot))
					})
			)
		});
	}

	filtersToUrl(filters: OrderFilters): any {
		return {
			ids: filters.ids || undefined,
			createDateFrom: this.utils.dateToString(filters.creationDate?.from) || undefined,
			createDateTo: this.utils.dateToString(filters.creationDate?.to) || undefined,
			dueDateFrom: this.utils.dateToString(filters.dueDate?.from) || undefined,
			dueDateTo: this.utils.dateToString(filters.dueDate?.to) || undefined,
			completionDateFrom: this.utils.dateToString(filters.completionDate?.from) || undefined,
			completionDateTo: this.utils.dateToString(filters.completionDate?.to) || undefined,
			photographers: filters.photographers?.map((p) => p.id) || undefined,
			statuses: filters.statuses?.map((s) => s.id) || undefined,
			page: filters.page || 1,
			size: filters.size || 20
		};
	}

	urlToFilters(url: any) {
		return new OrderFilters({
			ids: url.ids ? (Array.isArray(url.ids) ? url.ids.map((id: any) => Number(id)) : [Number(url.ids)]) : undefined,
			dueDate: url.dueDateFrom && url.dueDateTo ? new DateRange({ from: new Date(url.dueDateFrom), to: new Date(url.dueDateTo) }) : undefined,
			creationDate: url.createDateFrom && url.createDateTo ? new DateRange({ from: new Date(url.createDateFrom), to: new Date(url.createDateTo) }) : undefined,
			completionDate: url.completionDateFrom && url.completionDateTo ? new DateRange({ from: new Date(url.completionDateFrom), to: new Date(url.completionDateTo) }) : undefined,
			photographers: url.photographers ? (Array.isArray(url.photographers) ? url.photographers.map((id: any) => new Photographer({ id: Number(id) })) : [new Photographer({ id: Number(url.photographers) })]) : undefined,
			statuses: url.statuses ? (Array.isArray(url.statuses) ? url.statuses.map((id: any) => new Status({ id: Number(id) })) : [new Status({ id: Number(url.statuses) })]) : undefined,
			page: url.page ? Number(url.page) : 1,
			size: url.size ? Number(url.size) : 20
		});
	}

	toOrderStatus(orderStatus: any) {
		return new Status({
			id: orderStatus.id,
			name: orderStatus.name
		});
	}
}
